import * as turf from '@turf/turf';
import geoDataCache from './geoDataCache';

/**
 * Finds all GeoJSON features that intersect with the given point.
 * Uses preloaded cache if available, otherwise falls back to fetching.
 * @param {GeoJSON.Position|GeoJSON.Point} point - The point to test (as [lng, lat] or GeoJSON Point).
 * @returns {Array<{fileName: string, feature: object}>} Array of intersecting features with their source file.
 */
export async function findIntersectingFeatures(point) {
    // Accept [lng, lat] or GeoJSON Point
    const pt = Array.isArray(point)
        ? turf.point(point)
        : (point.type === 'Point' ? point : null);
    if (!pt) throw new Error('Invalid point format');

    // Wait for cache to be ready if it's still loading
    if (!geoDataCache.isReady()) {
        console.log('Waiting for GeoJSON cache to load...');
        await geoDataCache.preload();
    }

    const results = [];
    const cachedFiles = geoDataCache.getAll();

    // If cache is empty, throw error - data should have been preloaded
    if (cachedFiles.length === 0) {
        throw new Error('GeoJSON data not loaded. Please refresh the page and try again.');
    }

    // Process cached files
    for (const { file, data } of cachedFiles) {
        try {
            const features = data.features || (Array.isArray(data) ? data : [data]);
            
            for (const feature of features) {
                try {
                    if (turf.booleanPointInPolygon(pt, feature)) {
                        results.push({ fileName: file, feature });
                    }
                } catch (err) {
                    console.error(`Error testing feature in ${file}:`, err);
                }
            }
        } catch (e) {
            console.error(`Error processing cached file ${file}:`, e);
        }
    }
    
    return results;
}

export default findIntersectingFeatures;